import { createContext, useContext, useState, type ReactNode } from 'react';
import type { KissType } from '../lib/api';

/**
 * Multi-step wizard state. Lives at the App root so user can navigate
 * back/forward between Photos → Scene → Type → Duration without losing
 * choices. Cleared after successful payment (or by Reset).
 */
export interface WizardState {
  photo1: File | null;
  photo2: File | null;
  scene: string | null;        // scene.id
  kissType: KissType | null;
  durationSec: number | null;
}

interface WizardContextValue extends WizardState {
  setPhoto1: (f: File | null) => void;
  setPhoto2: (f: File | null) => void;
  setScene: (id: string) => void;
  setKissType: (t: KissType) => void;
  setDurationSec: (d: number) => void;
  reset: () => void;
}

const WizardContext = createContext<WizardContextValue | null>(null);

const initial: WizardState = {
  photo1: null,
  photo2: null,
  scene: null,
  kissType: null,
  durationSec: null,
};

export function WizardProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<WizardState>(initial);

  const value: WizardContextValue = {
    ...state,
    setPhoto1: (photo1) => setState((s) => ({ ...s, photo1 })),
    setPhoto2: (photo2) => setState((s) => ({ ...s, photo2 })),
    setScene: (scene) => setState((s) => ({ ...s, scene })),
    setKissType: (kissType) => setState((s) => ({ ...s, kissType })),
    setDurationSec: (durationSec) => setState((s) => ({ ...s, durationSec })),
    reset: () => setState(initial),
  };

  return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>;
}

export function useWizard() {
  const ctx = useContext(WizardContext);
  if (!ctx) throw new Error('useWizard must be used inside WizardProvider');
  return ctx;
}
